import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from '@mui/material';
import {
  Phone,
  AccessTime,
  Email,
  LocationOn,
  FitnessCenter,
  Group,
  TrendingUp,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import { WaitlistEntry, WaitlistFilter } from './types';
import {
  StatCard,
  SearchBar,
  FilterPills,
  DATA_GRID_SX,
  filterCardSx,
  tableCardSx,
  formatSubmittedDate,
  STAT_CARDS_GRID_SX,
  DATA_GRID_CONTAINER_SX,
} from './shared';

const WAITLIST_FILTERS: { value: WaitlistFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sweat_tour', label: 'SweatTour' },
  { value: 'city', label: 'City' },
];

export function WaitlistTab() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<WaitlistFilter>('all');
  const [stats, setStats] = useState<{ total?: number; sweatTour?: number; city?: number } | null>(
    null
  );

  useEffect(() => {
    fetchWaitlistStats();
  }, []);

  useEffect(() => {
    fetchWaitlistEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const skipInitialEmptySearch = useRef(true);

  useEffect(() => {
    if (skipInitialEmptySearch.current && searchTerm === '') {
      skipInitialEmptySearch.current = false;
      return;
    }
    skipInitialEmptySearch.current = false;

    const timeoutId = setTimeout(() => {
      fetchWaitlistEntries();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchWaitlistEntries = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: Record<string, string> = {
        page: '1',
        pageSize: '1000',
      };
      if (typeFilter !== 'all') {
        params.waitlistType = typeFilter;
      }

      const response = await apiService.getWaitlistEntries(params);
      let entriesData: WaitlistEntry[] = response.waitlists || [];

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase().trim();
        entriesData = entriesData.filter((entry) =>
          entry.name?.toLowerCase().includes(searchLower) ||
          entry.email?.toLowerCase().includes(searchLower) ||
          entry.phoneNumber?.toLowerCase().includes(searchLower) ||
          entry.city?.toLowerCase().includes(searchLower) ||
          entry.workoutInterest?.toLowerCase().includes(searchLower)
        );
      }

      entriesData.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.updatedAt).getTime();
        const dateB = new Date(b.createdAt || b.updatedAt).getTime();
        return dateB - dateA;
      });

      setEntries(entriesData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch waitlist entries');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWaitlistStats = async () => {
    try {
      const response = await apiService.getWaitlistStats();
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch waitlist stats:', err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: colors.textPrimary,
            fontWeight: 600,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      width: 160,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={colors.textSecondary}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Phone sx={{ fontSize: 14 }} />
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={colors.textSecondary}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <Email sx={{ fontSize: 14, flexShrink: 0 }} />
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'waitlistType',
      headerName: 'Type',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value === 'sweat_tour' ? 'SweatTour' : 'City'}
          size="small"
          sx={{
            backgroundColor: alpha(
              params.value === 'sweat_tour' ? colors.primary : colors.warning,
              0.1
            ),
            color: params.value === 'sweat_tour' ? colors.primary : colors.warning,
            border: `1px solid ${alpha(
              params.value === 'sweat_tour' ? colors.primary : colors.warning,
              0.3
            )}`,
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 28,
          }}
        />
      ),
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
      renderCell: (params) => {
        const row = params.row as WaitlistEntry;
        if (row.waitlistType === 'sweat_tour' || !row.city) return '-';
        return (
          <Typography
            variant="body2"
            color={colors.textSecondary}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <LocationOn sx={{ fontSize: 14 }} />
            {row.city}
          </Typography>
        );
      },
    },
    {
      field: 'workoutInterest',
      headerName: 'Workout Interest',
      width: 180,
      renderCell: (params) => {
        const row = params.row as WaitlistEntry;
        if (row.waitlistType === 'sweat_tour' || !row.workoutInterest) return '-';
        return (
          <Typography
            variant="body2"
            color={colors.textSecondary}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <FitnessCenter sx={{ fontSize: 14 }} />
            {row.workoutInterest}
          </Typography>
        );
      },
    },
    {
      field: 'joinedWhatsapp',
      headerName: 'WhatsApp',
      width: 120,
      renderCell: (params) => {
        const row = params.row as WaitlistEntry;
        if (row.waitlistType === 'sweat_tour') return '-';
        return (
          <Chip
            label={row.joinedWhatsAppCommunity ? 'Yes' : 'No'}
            size="small"
            sx={{
              backgroundColor: alpha(
                row.joinedWhatsAppCommunity ? colors.success : colors.textSecondary,
                0.1
              ),
              color: row.joinedWhatsAppCommunity ? colors.success : colors.textSecondary,
              border: `1px solid ${alpha(
                row.joinedWhatsAppCommunity ? colors.success : colors.textSecondary,
                0.3
              )}`,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
            }}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      width: 180,
      renderCell: (params) => {
        const { date, time } = formatSubmittedDate(params.value);
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTime sx={{ fontSize: 18, color: colors.textSecondary }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {date}
              </Typography>
              <Typography variant="caption" color={colors.textSecondary} display="block">
                {time}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  if (isLoading && !entries.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totalEntries = stats?.total ?? entries.length;
  const sweatTourCount =
    stats?.sweatTour ?? entries.filter((e) => e.waitlistType === 'sweat_tour').length;
  const cityCount = stats?.city ?? entries.filter((e) => e.waitlistType === 'city').length;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={STAT_CARDS_GRID_SX}>
        <StatCard
          label="Total Entries"
          value={totalEntries}
          icon={<TrendingUp sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="SweatTour"
          value={sweatTourCount}
          subtitle={
            totalEntries > 0
              ? `${Math.round((sweatTourCount / totalEntries) * 100)}% of total`
              : undefined
          }
          icon={<Group sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="City"
          value={cityCount}
          subtitle={
            totalEntries > 0 ? `${Math.round((cityCount / totalEntries) * 100)}% of total` : undefined
          }
          icon={<LocationOn sx={{ fontSize: 28, color: colors.warning }} />}
          color={colors.warning}
        />
      </Box>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search by name, email, or phone..."
      />

      <Card sx={filterCardSx}>
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <FilterPills options={WAITLIST_FILTERS} value={typeFilter} onChange={setTypeFilter} />
        </CardContent>
      </Card>

      <Card sx={tableCardSx}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={DATA_GRID_CONTAINER_SX}>
            <DataGrid
              rows={entries}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              getRowHeight={() => 75}
              sx={DATA_GRID_SX}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
